import {
  Box,
  Button,
  FieldError,
  Flexbox,
  Label,
  Stack,
  StackRow,
  shootToast,
  BoxFileMultiple,
} from "codekit";
import { useForm } from "react-hook-form";
import { useRef, useState, useEffect } from "react";
import {
  useCreatePackagePresignedMutation,
  useConfirmPackagePresignedMutation,
  useCreatePackageMutation,
} from "../../libs/redux/slices/Genioxxx/genioxxxApiSlice";

const Carguedo = ({
  setShowGenioModal,
  genioParams,
  jsonMetx,
  onUploadSuccess,
  sendOnDrop = true,
}) => {
  const uploadRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [isSending, setIsSending] = useState(false);

  const [createPackagePres] = useCreatePackagePresignedMutation();
  const [createPackage] = useCreatePackageMutation();
  const [confirmPackagePresigned] = useConfirmPackagePresignedMutation();

  const methods = useForm({
    mode: "onBlur",
    defaultValues: { files: [] },
  });


  const {
    setValue,
    formState: { errors },
  } = methods;

  const tresMegas = 3 * 1024 * 1024;

  const fileToBase64Payload = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = String(reader.result || "");
        const base64 = dataUrl.split(",")[1] || "";
        const mime = file.type || "";
        resolve(`${mime},${base64}`);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const buildPayload = (file, filebs64) => ({
    ...genioParams,
    JSONMETX: jsonMetx,
    FILEBS64: filebs64,
    TYPEXXXX: file.type || "",
    NAMEXXXX: file.name || "",
  });

  const uploadFileToGenio = async (file) => {
    let IDARCHXX = null;
    let docUrl = null;
    let path = null;

    try {
      if (file.type === "application/pdf" && file.size > tresMegas) {
        // Caso 1: PDF > 3MB (usa Presigned URL)
        const dataPackagePres = {
          ...genioParams,
          JSONMETX: jsonMetx,
          TYPEXXXX: file.type,
          NAMEXXXX: file.name,
        };
        const response = await createPackagePres(dataPackagePres).unwrap();
        const { url: presignedUrl, IDARCHXX: idArchxxPres } = response.data.data;
        IDARCHXX = idArchxxPres;
        docUrl = presignedUrl;

        await fetch(docUrl, { method: "PUT", body: file, redirect: "follow" });
        await confirmPackagePresigned({ path: { IDARCHXX }, body: { REGESTXX: "ACTIVO" } }).unwrap();

        return {
          IDARCHXX,
          status: "success",
          fileName: file.name,
          docUrl,
          originalFile: file
        };

      } else {
        // Caso 2: Cualquier otro tipo de archivo o PDF <= 3MB (usa Base64)
        const filebs64 = await fileToBase64Payload(file);
        const payload = buildPayload(file, filebs64);

        const response = await createPackage(payload).unwrap();
        IDARCHXX = response.IDARCHXX;
        path = response.PATHFILX;
        docUrl = response.DOCDURLX;

        return {
          IDARCHXX,
          status: "success",
          fileName: file.name,
          docUrl,
          path,
          originalFile: file
        };
      }
    } catch (err) {
      console.error(`Error al cargar el archivo ${file.name}:`, err);
      return {
        status: "error",
        fileName: file.name,
        error: err,
        originalFile: file
      };
    }
  };

  const handleSendPackages = async (filesToSend) => {
    if (!filesToSend.length) return;

    setIsSending(true);

    for (const file of filesToSend) {
      const result = await uploadFileToGenio(file);
      setFiles(prev => {
        const updated = prev.filter(f => f !== file);
        setValue("files", updated);
        return updated;
      });
      if (result.status === "success") {
        shootToast({
          text: `${file.name} cargado exitosamente.`,
          variant: "success",
          autoHide: true,
          duration: 2000,
        });
      } else {
        shootToast({
          text: `Error al cargar ${file.name}.`,
          variant: "error",
          autoHide: true,
          duration: 4000,
        });
      }
      if (onUploadSuccess) {
        onUploadSuccess([result]);
      }
    }

    setIsSending(false);
    if (files.length === 0) {
      setShowGenioModal(false);
    }
  };


  const handleChangeFiles = (incoming) => {
    const newFiles = Array.isArray(incoming) ? incoming : [incoming];
    const onlyAllowed = newFiles.filter(isAllowed);

    if (onlyAllowed.length > 0) {
      const updatedFiles = [...files, ...onlyAllowed];
      setFiles(updatedFiles);
      setValue("files", updatedFiles, { shouldValidate: true });
    }

    const rejected = newFiles.filter(f => !isAllowed(f));
    if (rejected.length) {
      shootToast({
        variant: "warning",
        text: "Solo se permiten imágenes o archivos PDF. Se ignoraron algunos archivos.",
        duration: 3000,
        autoHide: true,
      });
    }
  };

  const isAllowed = (f) => {
    const type = f?.type || "";
    const name = f?.name || "";

    const allowedExtensions = ['.xls', '.doc', '.pdf', '.jpg', '.png', '.xml'];
    const fileExtension = name.substring(name.lastIndexOf('.')).toLowerCase();

    return (
      type.startsWith("image/") ||
      type === "application/pdf" ||
      allowedExtensions.includes(fileExtension)
    );
  };


  const handleRemoveFileMultiple = (index) => {
    const nextFiles = files.filter((_, i) => i !== index);
    setFiles(nextFiles);
    setValue("files", nextFiles);
  };

  const handleUploadFile = () => {
    uploadRef.current?.click();
  };

  const handleDrag = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    const droppedFiles = Array.from(event.dataTransfer?.files || []);
    handleChangeFiles(droppedFiles);
  };

  useEffect(() => {
    if (sendOnDrop && files.length > 0) {
      handleSendPackages(files);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendOnDrop, files]);

  return (
    <Box mt={4}>
      <Label>Adjuntar Soporte</Label>
      <Box
        border={1}
        borderColor="neutral-300"
        spacing={3}
        rounded="md"
        bg={`${dragActive ? "neutral-300" : "neutral-200"}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <Flexbox justifyContent='center' alignItems='center' minHeight={140}>
          <Stack gap={3} justifyItems='center'>
            <StackRow>
              <BoxFileMultiple
                bg="neutral-200"
                spacing={4}
                rounded="md"
                borderColor="neutral-400"
                maxFiles={10}
                onChangeFile={handleChangeFiles}
                onRemoveFile={handleRemoveFileMultiple}
              >
                <Box mt={4}>
                  <Stack justifyItems="center" gap={4}>
                    <Button
                      type="button"
                      variant="secondary-bold"
                      icon="files-plus"
                      onClick={handleUploadFile}
                      isLoading={isSending}
                      disabled={isSending}
                    >
                      {isSending ? "Cargando..." : "Seleccionar archivos"}
                    </Button>
                    <input
                      ref={uploadRef}
                      type="file"
                      multiple
                      accept=".xls,.doc,.pdf,.jpg,.png,.xml,image/*,application/pdf"
                      style={{ display: "none" }}
                      onChange={(e) => handleChangeFiles(Array.from(e.target.files || []))}
                    />
                  </Stack>
                </Box>
              </BoxFileMultiple>
            </StackRow>
          </Stack>
        </Flexbox>
      </Box>
      {errors.files && <FieldError>{errors.files.message}</FieldError>}
    </Box>
  );
};

export default Carguedo;
